import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Bot, Sparkles, Maximize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useChat } from "../../context/ChatContext";

export default function ChatBot() {
  const navigate = useNavigate();
  const { messages, isTyping, sendMessage } = useChat();

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isTyping, isOpen]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input);
    setInput("");
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            onClick={() => setIsOpen(true)}
            aria-label="Buka Chat AI"
            className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-[20px] flex items-center justify-center shadow-[0_8px_30px_rgb(212,149,14,0.4)] group overflow-hidden"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-gold-300 via-gold-500 to-gold-600 transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-[2px] bg-navy-950 rounded-[18px] flex items-center justify-center z-10">
              <Sparkles
                className="absolute top-3 right-3 text-gold-400 opacity-50"
                size={12}
              />
              <Bot
                size={28}
                className="text-gold-400 group-hover:text-gold-300 transition-colors"
              />
            </div>

            {/* Ping effect */}
            <span className="absolute -inset-2 rounded-[24px] animate-pulse opacity-20 bg-gold-500 -z-10" />

            {/* Tooltip */}
            <motion.span
              initial={{ opacity: 0, x: 10, scale: 0.9 }}
              whileHover={{ opacity: 1, x: 0, scale: 1 }}
              className="hidden sm:block absolute right-20 bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-semibold px-4 py-2.5 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap"
            >
              Tanya AI Assistant
            </motion.span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
              scale: 0.9,
              transformOrigin: "bottom right",
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", stiffness: 220, damping: 25 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-32px)] h-[550px] max-h-[calc(100vh-32px)] flex flex-col rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden border border-white/10 bg-navy-950/90 backdrop-blur-2xl"
          >
            {/* Glowing orb background effect inside chat */}
            <div className="absolute top-0 left-0 w-full h-40 bg-gold-500/10 blur-[50px] -z-10" />
            <div className="absolute bottom-0 right-0 w-full h-40 bg-navy-600/20 blur-[50px] -z-10" />

            {/* Header */}
            <div className="relative px-6 py-5 flex items-center justify-between border-b border-white/5 bg-gradient-to-r from-navy-900/80 to-navy-950/80 backdrop-blur-sm z-10">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-gold-300 to-gold-600 p-[1px]">
                    <div className="w-full h-full bg-navy-950 rounded-2xl flex items-center justify-center">
                      <Bot size={20} className="text-gold-400" />
                    </div>
                  </div>
                  <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 border-2 border-navy-950 rounded-full animate-pulse" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-bold tracking-wide">
                    Legal AI Assistant
                  </h3>
                  <p className="text-gold-400/80 text-xs font-medium flex items-center gap-1.5 mt-0.5">
                    Online &bull; Siap Membantu
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {/* Fullscreen button */}
                <button
                  onClick={() => navigate("/chatbot")}
                  aria-label="Buka Layar Penuh"
                  title="Buka di layar penuh"
                  className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-navy-300 hover:text-gold-400 hover:bg-white/10 transition-all"
                >
                  <Maximize2 size={15} />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Tutup Chat"
                  className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-navy-300 hover:text-white hover:bg-white/10 transition-all"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 px-5 py-6 overflow-y-auto scrollbar-hide flex flex-col gap-5 z-10">
              {messages.map((msg) => (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={msg.id}
                  className={`flex flex-col max-w-[85%] ${msg.isBot ? "self-start" : "self-end"}`}
                >
                  <div
                    className={`px-4 py-3 text-sm leading-relaxed shadow-lg ${
                      msg.isBot
                        ? "bg-navy-900/80 text-navy-50 rounded-2xl rounded-tl-sm border border-white/5 backdrop-blur-sm"
                        : "bg-gradient-to-br from-gold-400 to-gold-600 text-navy-950 rounded-2xl rounded-tr-sm font-medium"
                    }`}
                  >
                    {msg.text}
                  </div>

                  {/* REDIRECT BUTTON */}
                  {msg.isRedirect && msg.waUrl && (
                    <a
                      href={msg.waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 pt-2 flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2.5 px-4 rounded-xl shadow-md transition-all active:scale-95"
                    >
                      <Sparkles size={14} />
                      Lanjut ke WhatsApp
                    </a>
                  )}
                  <span
                    className={`text-[10px] text-navy-400 mt-1.5 px-1 ${msg.isBot ? "text-left" : "text-right"}`}
                  >
                    {msg.isBot ? "AI Assistant" : "Anda"} &bull;{" "}
                    {new Date(msg.id).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </motion.div>
              ))}

              {isTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col max-w-[85%] self-start"
                >
                  <div className="px-5 py-4 bg-navy-900/80 rounded-2xl rounded-tl-sm border border-white/5 backdrop-blur-sm shadow-lg flex gap-1.5 items-center">
                    <motion.span
                      animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                      transition={{ repeat: Infinity, duration: 1.2, delay: 0 }}
                      className="w-1.5 h-1.5 bg-gold-400 rounded-full"
                    />
                    <motion.span
                      animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.2,
                        delay: 0.2,
                      }}
                      className="w-1.5 h-1.5 bg-gold-400 rounded-full"
                    />
                    <motion.span
                      animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.2,
                        delay: 0.4,
                      }}
                      className="w-1.5 h-1.5 bg-gold-400 rounded-full"
                    />
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} className="h-2" />
            </div>

            {/* Input Area */}
            <div className="p-5 pt-2 relative z-10 bg-gradient-to-t from-navy-950 via-navy-950/95 to-transparent">
              <form
                onSubmit={handleSend}
                className="relative flex items-center"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ketik pertanyaan hukum Anda..."
                  className="w-full bg-navy-900/60 text-white text-sm rounded-full pl-5 pr-14 py-3.5 outline-none border border-white/10 focus:border-gold-500/50 focus:bg-navy-900/90 transition-all placeholder-navy-400 shadow-inner backdrop-blur-md"
                />
                <button
                  type="submit"
                  aria-label="Kirim Pesan"
                  disabled={!input.trim()}
                  className="absolute right-1.5 w-9 h-9 rounded-full bg-gold-gradient flex items-center justify-center text-navy-950 disabled:opacity-50 disabled:grayscale transition-all shadow-md"
                >
                  <Send size={15} className="ml-0.5" />
                </button>
              </form>
              <div className="text-center mt-3">
                <span className="text-[10px] text-navy-400 flex items-center justify-center gap-1">
                  <Sparkles size={10} className="text-gold-500" /> AI dapat
                  melakukan kesalahan. Harap verifikasi.
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
